import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import requests from "./api/client"
import { useForm } from "./Form"
import { useInitReq } from "./InitReq"
import { useSettings } from "./Settings"
import { limparCheckout } from "./utils/card/checkoutPersistido"

/** Página do wizard; é a única em que uma compra com cartão guardada pode ser retomada. */
const PAGINA_CHECKOUT = "configure-seu-plano"

export interface Plan {
  id: number
  plano: string
  categoria?: string
  categoria_id?: string
  valor: number
  nome_plano: string
  ligacoes: string
  sms: string
  detalhamento: string
  descricao_detalhamento: string
  cor: string
  internet_padrao: string
  beneficios: string[]
  aplicativos: string[]
}

export interface PlansContext {
  plans: Plan[]
  currentPlan: Plan
  chipOnly: boolean
  chipQuantity: number
  planClass: string
  monthlyPrice: number
  annualPrice: number
  chipOnlyPrice: number
  chosenPlan: string
  pickUpChip: boolean
  virtualChip: boolean
  portability: boolean
  smartControl: boolean
  planConfigs: boolean
  planBonus: boolean
  planIndex: number
  step: number
  steps: string[]
  loadingPlan: boolean
  currentSliderValue: number
  currentShippingPrice: string
  currentChipPrice: string
  totalPrice: number
  clientId: number
  updatePlanState: (updatedValues: Partial<PlansContext>) => void
  setvirtualChip: (virtualChip: boolean) => void
  setPortability: (portability: boolean) => void
  setSmartControl: (smartControl: boolean) => void
}

export const defaultPlan: Plan = {
  id: 0,
  plano: "",
  valor: 0,
  nome_plano: "",
  ligacoes: "",
  sms: "",
  detalhamento: "",
  descricao_detalhamento: "",
  cor: "",
  internet_padrao: "",
  beneficios: [""],
  aplicativos: [""]
}

const defaultPlansData: PlansContext = {
  plans: [],
  currentPlan: defaultPlan,
  chipOnly: false,
  chipQuantity: 1,
  planClass: "plan1",
  monthlyPrice: 39.9,
  annualPrice: 26.6,
  chipOnlyPrice: 0,
  chosenPlan: "monthly",
  pickUpChip: false,
  virtualChip: false,
  portability: true,
  smartControl: false,
  planConfigs: true,
  planBonus: true,
  planIndex: 0,
  step: 0,
  steps: ["Seu Plano", "Informações", "Endereço", "Método de pagamento", "Pagamento"],
  loadingPlan: true,
  currentSliderValue: 0,
  currentShippingPrice: "0,00",
  currentChipPrice: "0,00",
  totalPrice: 0,
  clientId: 0,
  updatePlanState: () => {},
  setvirtualChip: (virtualChip: boolean) => {},
  setPortability: (portability: boolean) => {},
  setSmartControl: (smartControl: boolean) => {}
}

const PlanContext = createContext<PlansContext>(defaultPlansData)

export function usePlan() {
  return useContext(PlanContext)
}

export function PlanProvider({ children }: PropsWithChildren) {
  const { currentFields: { cartNavigate, hash }, updateFormState } = useForm()
  const { updateInitState } = useInitReq()
  const { isSurfMVNE } = useSettings()
  const [currentState, setCurrentState] = useState<PlansContext>(defaultPlansData)
  const [currentPath, setCurrentPath] = useState<string>()
  const paginaAnteriorRef = useRef<string>()
  const location = useLocation()
  const colorPlanClasses = ["plan1", "plan2", "plan3", "plan4", "plan5"]

  const updatePlanState = (updatedValues: Partial<PlansContext>) => {
    setCurrentState((prevState) => ({
      ...prevState,
      ...updatedValues,
    }))
  }

  const setvirtualChip = useCallback((virtualChip: boolean) => {
    setCurrentState((prevState) => ({
      ...prevState,
      virtualChip: virtualChip,
      planConfigs: virtualChip || prevState.portability || prevState.smartControl
    }))
  }, [])

  const setPortability = useCallback((portability: boolean) => {
    setCurrentState((prevState) => ({
      ...prevState,
      portability: portability,
      planBonus: isSurfMVNE ? false : (portability || prevState.smartControl),
      planConfigs: prevState.virtualChip || portability || prevState.smartControl,
    }))
  }, [isSurfMVNE])

  const setSmartControl = useCallback((smartControl: boolean) => {
    setCurrentState((prevState) => ({
      ...prevState,
      smartControl: smartControl,
      planBonus: isSurfMVNE ? false : (prevState.portability || smartControl),
      planConfigs: prevState.virtualChip || prevState.portability || smartControl
    }))
  }, [isSurfMVNE])

  useEffect(() => {
    const param = location.pathname.split("/")[1]
    setCurrentPath(param)
  }, [location])

  /*
   * A compra com cartão guardada só cobre o F5 na própria página de checkout.
   * Página carregada em outra rota descarta o que estava guardado. Sair da página
   * de checkout no passo 3, ou no passo 4 com uma compra com cartão pendente,
   * começa um fluxo novo: compra, carrinho e cupom são descartados (o carrinho
   * antigo reaplicaria o plano anterior), o wizard fica no passo 3 e o próximo
   * avanço cria carrinho, compra e OTP novos com o plano atual.
   */
  useEffect(() => {
    if (currentPath === undefined) return
    const anterior = paginaAnteriorRef.current
    paginaAnteriorRef.current = currentPath

    if (anterior === undefined) {
      if (currentPath !== PAGINA_CHECKOUT) limparCheckout()
      return
    }

    if (anterior === PAGINA_CHECKOUT && currentPath !== PAGINA_CHECKOUT && (currentState.step === 3 || hash)) {
      limparCheckout()
      updateFormState({ hash: '', purchaseSummaryWithOtp: null, sdk3DSIndisponivel: false, errorSummary: null, cartId: '' })
      updateInitState({ cartInfo: undefined, cartSearchLiberated: false, couponValid: false, couponValue: 0, couponText: '' })
      setCurrentState((prevState) => (prevState.step === 4 ? { ...prevState, step: 3 } : prevState))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath])

  useEffect(() => {
    setCurrentState((prevState) => ({
      ...prevState,
      planClass: colorPlanClasses[prevState.planIndex],
      monthlyPrice: currentState.currentPlan.valor || prevState.monthlyPrice,
      annualPrice: ((currentState.currentPlan.valor || prevState.monthlyPrice) * 8) / 12,
    }))
    // eslint-disable-next-line
  }, [currentState.currentPlan])

  useEffect(() => {
    setCurrentState((prevState) => {
      const updateInternetString = (value: string) => {
        const match = value.match(/(\d+)\s*(GB|MB)/i)
        if (!match) return value
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [_, number, unit] = match
        const newNumber = parseInt(number) + 5
        return `${newNumber} ${unit}`
      }

      const originalPlans = prevState.plans.map((plan, index) => ({
        ...plan,
        plano: prevState.plans[index]?.internet_padrao || plan.plano,
      }))

      // Para MVNEs Surf (isSurfMVNE=true) não aplicar bônus +5GB: mantém os planos originais
      const shouldApplyBonus = prevState.portability && !isSurfMVNE

      const newPlans = shouldApplyBonus
        ? prevState.plans.map((plan) => ({
            ...plan,
            plano: updateInternetString(plan.plano),
          }))
        : originalPlans

      const newPlan = shouldApplyBonus
        ? updateInternetString(prevState.currentPlan.plano)
        : prevState.plans[prevState.planIndex]?.internet_padrao || prevState.currentPlan.plano

      return {
        ...prevState,
        currentPlan: { ...prevState.currentPlan, plano: newPlan },
        plans: newPlans,
      }
    })
  }, [currentState.portability, isSurfMVNE])

  useEffect(() => {
    async function fetchPlans() {
      if (currentState.plans.length > 0) return

      try {
        const plansArray = await requests.plansList()

        if (Array.isArray(plansArray)) {
          // Para MVNEs Surf, nunca aplicar o bônus +5GB (nem por default da portabilidade)
          const applyBonus = defaultPlansData.portability && !isSurfMVNE

          const adjustPlanInternet = (plan: Plan, shouldBonus: boolean) => {
            const match = plan.internet_padrao.match(/(\d+)\s*(GB|MB)/i)
            if (!match) return plan.plano
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [_, number, unit] = match
            const adjustedNumber = shouldBonus ? parseInt(number) + 5 : parseInt(number)
            return `${adjustedNumber} ${unit}`
          }

          const adjustedPlans = plansArray.map((plan) => ({
            ...plan,
            plano: adjustPlanInternet(plan, applyBonus),
          }))

          const firstPlan = adjustedPlans[0] || defaultPlan

          const updatedCurrentPlan =
            currentState.currentPlan?.id === 0
              ? { ...firstPlan, plano: adjustPlanInternet(firstPlan, applyBonus) }
              : { ...currentState.currentPlan, plano: adjustPlanInternet(currentState.currentPlan, applyBonus) }

          setCurrentState((prevState) => ({
            ...prevState,
            plans: adjustedPlans,
            currentPlan: updatedCurrentPlan,
            planClass: colorPlanClasses[prevState.planIndex],
            monthlyPrice: adjustedPlans[0]?.valor || prevState.monthlyPrice,
            annualPrice: ((adjustedPlans[0]?.valor || prevState.monthlyPrice) * 8) / 12,
            loadingPlan: false,
            // Para MVNEs Surf, planBonus sempre false (não há bônus +5GB)
            planBonus: isSurfMVNE ? false : prevState.planBonus,
          }))
        } else {
          console.error("Resposta inesperada da API:", plansArray)
        }
      } catch (error) {
        console.error("Erro ao buscar planos:", error)
      }
    }

    if (currentPath === "" || currentPath === "vip" || currentPath === "configure-seu-plano") {
      fetchPlans()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, isSurfMVNE])
  
  useEffect(() => {
    if (currentState.step === 3) updateInitState({ cartSearchLiberated: true })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState.step])

  useEffect(() => {
    if (cartNavigate) setCurrentState((prevState) => ({ ...prevState, step: 3 }))
  },[cartNavigate])

  return (
    <PlanContext.Provider
      value={{
        ...currentState,
        updatePlanState,
        setvirtualChip,
        setPortability,
        setSmartControl
      }}
    >
      {children}
    </PlanContext.Provider>
  )
}